import { formatPhoneNumber } from "@repo/lib";
import { Edit1, Phone, Share } from "@repo/icons";
import { CardHeader, CardRoot, CardContent } from "@/components/card";
import { AssistProBadge } from "@/routes/settings/assist/-components/assist-pro-badge";

interface PhoneNumberCardRootProps {
  children: React.ReactNode;
  className?: string;
}

export const PhoneNumberCardRoot = ({
  children,
  className,
}: PhoneNumberCardRootProps): JSX.Element => (
  <CardRoot>
    <CardContent className={className}>{children}</CardContent>
  </CardRoot>
);

export const AssistProPhoneCardHeader = (): JSX.Element => (
  <CardHeader className="py-0 px-0 flex flex-row justify-between items-center w-full gap-2">
    <p className="text-xs text-neutral-600 font-medium">
      Seu telefone exclusivo está pronto para uso! Ligue e ouça a mensagem de atendimento
      que seus pacientes vão ouvir ao te ligar 😉
    </p>
    <AssistProBadge />
  </CardHeader>
);

export const PhoneNumberPendingHeader = (): JSX.Element => (
  <CardHeader className="py-0 px-0 flex flex-row justify-between items-center w-full gap-2">
    <p className="text-xs text-neutral-600 font-medium">
      Seu telefone exclusivo está a caminho. Vamos te avisar assim que ele for ativado!
    </p>
    <AssistProBadge />
  </CardHeader>
);

export const PhoneNumberPendingSection = (): JSX.Element => (
  <div className="h-12 w-full flex flex-row justify-between items-center bg-neutral-50 py-4 px-3 mt-3">
    <p className="text-neutral-400 text-xs font-medium">(00) 0000-0000</p>
    <Share className="fill-neutral-200" size={24} />
  </div>
);

interface PhoneNumberSectionProps {
  ramalTroncoNormalizado: string;
  onShare: () => void;
}

export const PhoneNumberSection = ({
  ramalTroncoNormalizado,
  onShare,
}: PhoneNumberSectionProps): JSX.Element => (
  <div className="h-12 w-full flex flex-row justify-between items-center bg-neutral-50 py-4 px-3 mt-3">
    <p className="text-neutral-500 text-xs font-medium">
      {formatPhoneNumber(ramalTroncoNormalizado.replace("+55", ""))}
    </p>
    <button
      type="button"
      onClick={onShare}
      data-testid="assist-pro-phone-number-share-button"
    >
      <Share className="fill-secondary-400" size={24} />
    </button>
  </div>
);

interface PublicPhoneNumberCardHeaderProps {
  hasNumber: boolean;
}

export const PublicPhoneNumberCardHeader = ({
  hasNumber,
}: PublicPhoneNumberCardHeaderProps): JSX.Element => (
  <CardHeader className="py-0 px-0 flex flex-row items-center w-full gap-2">
    <div className="flex flex-row items-center gap-2">
      <div className="h-6 w-6 flex items-center justify-center bg-primary-50 rounded-full">
        <Phone className="fill-primary-400" />
      </div>
      <p className="text-xs text-neutral-600 font-semibold">Telefone para pacientes</p>
    </div>
    <div className="flex flex-row items-center justify-center text-secondary-400 font-medium text-xs">
      <Edit1 className="fill-secondary-400" />
      {hasNumber ? "Alterar" : "Adicionar"}
    </div>
  </CardHeader>
);

interface PublicPhoneNumberCardDescriptionProps {
  hasNumber: boolean;
}

export const PublicPhoneNumberCardDescription = ({
  hasNumber,
}: PublicPhoneNumberCardDescriptionProps): JSX.Element => (
  <p className="text-xs text-neutral-600 font-medium">
    {hasNumber ? (
      <>
        Seus pacientes usam esse telefone para falar com você.{" "}
        <strong>Mantenha atualizado para não perder contatos.</strong>
      </>
    ) : (
      <>
        Seus pacientes usam esse telefone para falar com você.{" "}
        <strong>Adicione um número para não perder agendamentos.</strong>
      </>
    )}
  </p>
);

export const PublicPhoneNumberSection = ({ phone }: { phone: string }): JSX.Element => (
  <div className="h-12 w-full flex flex-row justify-between items-center bg-neutral-50 py-4 px-3 mt-3">
    <span className="text-neutral-500 text-xs font-medium">
      {formatPhoneNumber(phone)}
    </span>
  </div>
);

export const PublicPhoneNumberPendingSection = (): JSX.Element => (
  <div className="h-12 w-full flex flex-row justify-between items-center bg-neutral-50 py-4 px-3 mt-3">
    <span className="text-neutral-400 text-xs font-medium">(00) 0000-0000</span>
  </div>
);
